/**
 * Export helpers for the map HUD cards (Story / Sentinel): plain text /
 * markdown, a PNG of the card, and a small PowerPoint deck. Built so the
 * content can be dropped into briefings or other media.
 */

import { loadHtml2Canvas, loadPptxGenJS } from '@signals/app-shell/utils/exportLibs';
import { captureMapScreenshot } from '@signals/app-shell/utils/powerpointExport';
import type { NarrativeChatMessage, StoryEvent } from '../../stores/narrativeStore';

export interface HudExportSection {
  heading: string;
  /** Paragraphs / bullet lines. */
  lines: string[];
}

export interface HudExportDoc {
  title: string;
  subtitle?: string;
  /** Classification / handling banner printed at the top and bottom of every output. */
  classification?: string;
  sections: HudExportSection[];
}

const stamp = () => new Date().toISOString().slice(0, 16).replace('T', '-').replace(':', '');
const safeName = (s: string) => s.replace(/[^a-z0-9-_]+/gi, '-').replace(/-+/g, '-').replace(/^-|-$/g, '').toLowerCase();

const fmtDuration = (seconds: number): string => {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m`;
  return `${Math.max(0, Math.round(seconds))}s`;
};
const fmtMiles = (km: number): string => {
  const mi = km * 0.621371;
  return mi < 0.1 ? `${Math.round(mi * 5280)} ft` : `${mi.toFixed(2)} mi`;
};
const fmtWhen = (d: Date) =>
  `${d.toLocaleDateString([], { month: 'short', day: 'numeric' })} ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
const fmtTime = (d: Date) => d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

/* ------------------------------------------------------------------------ */
/* Document builders                                                         */
/* ------------------------------------------------------------------------ */

export function buildStoryDoc(
  events: StoryEvent[],
  entities: string[],
  aiSummary: string | null,
  aiFocus: string | null = null,
): HudExportDoc {
  const travel = events.filter((e) => e.type === 'travel');
  const dwell = events.filter((e) => e.type === 'dwell');
  const cameras = events.filter((e) => e.type === 'camera');
  const secs = (e: StoryEvent) => (e.endTime.getTime() - e.startTime.getTime()) / 1000;
  const span =
    events.length > 0
      ? `${fmtWhen(events[0].startTime)} → ${fmtWhen(events[events.length - 1].endTime)}`
      : '';
  const sections: HudExportSection[] = [];
  if (events.length > 0) {
    sections.push({
      heading: 'Summary',
      lines: [
        `Events: ${events.length} (${dwell.length} dwells, ${travel.length} trips${cameras.length ? `, ${cameras.length} camera passes` : ''})`,
        `Distance traveled: ${fmtMiles(travel.reduce((s, e) => s + (e.distance || 0), 0))}`,
        `Time dwelling: ${fmtDuration(dwell.reduce((s, e) => s + secs(e), 0))}`,
        span ? `Span: ${span}` : '',
      ].filter(Boolean),
    });
    sections.push({
      heading: 'Timeline',
      lines: events.map((e, i) => {
        const n = e.seq ?? i + 1;
        const when = `${fmtWhen(e.startTime)} → ${fmtTime(e.endTime)}`;
        if (e.type === 'dwell') {
          const visits = e.visitCount && e.visitCount > 1 ? ` (${e.visitCount} visits)` : '';
          return `${n}. Dwelling ${fmtDuration(e.duration ?? secs(e))}${visits} — ${when}${e.address ? ` — ${e.address}` : ''}`;
        }
        if (e.type === 'camera') {
          const dist = e.distanceM !== undefined ? ` — ${Math.round(e.distanceM)} m from track` : '';
          const hits = e.hits && e.hits > 1 ? ` (${e.hits} hits)` : '';
          return `${n}. Camera ${e.address || e.cameraId || ''}${e.category ? ` [${e.category}]` : ''}${hits} — ${fmtWhen(e.startTime)}${dist}`;
        }
        const trips = e.tripCount && e.tripCount > 1 ? ` (${e.tripCount} trips)` : '';
        const dist = e.distance !== undefined ? ` ${fmtMiles(e.distance)}` : '';
        const pts = e.pointCount ? `${e.pointCount} GPS points` : 'inferred between dwells';
        return `${n}. Traveling${dist}${trips} — ${when} — ${fmtDuration(secs(e))}, ${pts}`;
      }),
    });
  }
  if (aiSummary) {
    sections.push({
      heading: aiFocus ? `AI summary — focus: ${aiFocus}` : 'AI summary',
      lines: aiSummary.split(/\n{2,}/).map((p) => p.trim()).filter(Boolean),
    });
  }
  return {
    title: 'Story timeline',
    subtitle: `${entities.join(', ')}${span ? ` · ${span}` : ''}`,
    sections,
  };
}

/** Export doc for the stand-alone AI summary card. */
export function buildStoryAiDoc(entities: string[], aiSummary: string | null, aiFocus: string | null = null): HudExportDoc {
  return {
    title: 'AI summary',
    subtitle: [entities.join(', '), aiFocus ? `focus: ${aiFocus}` : ''].filter(Boolean).join(' · ') || undefined,
    sections: aiSummary
      ? [{ heading: aiFocus ? `Focus: ${aiFocus}` : 'Summary', lines: aiSummary.split(/\n{2,}/).map((p) => p.trim()).filter(Boolean) }]
      : [],
  };
}

/* ------------------------------------------------------------------------ */
/* Product (whole map view + every widget)                                   */
/* ------------------------------------------------------------------------ */

export interface ProductRouteSequenceEntry {
  seq: number;
  kind: 'camera' | 'dwell';
  entityId: string;
  time: Date;
  endTime: Date;
  label: string;
  detail: string;
  lon: number;
  lat: number;
  cameraId?: string;
  categoryLabel?: string;
  distanceM?: number;
  hits?: number;
}

export interface ProductLegendCategory {
  label: string;
  count: number;
}

export interface ProductDocInput {
  /** Analyst title; defaults to "Pathfinder product". */
  title?: string;
  /** Classification / handling caveat shown as a banner (e.g. "UNCLASSIFIED // FOUO"). */
  classification?: string;
  /** Free-text analyst notes placed first. */
  notes?: string;
  entities: string[];
  /** Story sequence (already cropped to the map view if the user chose that). */
  events: StoryEvent[] | null;
  /** Total events before cropping, when different from `events.length`. */
  eventsTotal?: number;
  aiSummary?: string | null;
  aiFocus?: string | null;
  routeSequence?: Array<{ entityId: string; entries: ProductRouteSequenceEntry[] }> | null;
  camerasPassed?: Array<{
    seq: number;
    entityId: string;
    cameraId?: string;
    label: string;
    category?: string | null;
    time: string;
    endTime: string;
    lat: number;
    lng: number;
    distanceM?: number;
    hits?: number;
  }> | null;
  legend?: { cameraCategories: ProductLegendCategory[]; cameraCount: number } | null;
  /** Human text for the map view, e.g. "cropped to map view" or "full dataset". */
  viewNote?: string;
  include: {
    story: boolean;
    ai: boolean;
    routeSequence: boolean;
    cameras: boolean;
    legend: boolean;
  };
}

const fmtLatLng = (lat: number, lng: number) => `${lat.toFixed(5)}, ${lng.toFixed(5)}`;

/** One document for a briefing product: notes → story → AI → route sequence → cameras → legend. */
export function buildProductDoc(input: ProductDocInput): HudExportDoc {
  const sections: HudExportSection[] = [];
  const entities = input.entities;
  const events = input.events ?? [];

  if (input.notes?.trim()) {
    sections.push({ heading: 'Analyst notes', lines: input.notes.trim().split(/\n{2,}/).map((p) => p.trim()).filter(Boolean) });
  }

  const scope: string[] = [];
  if (entities.length) scope.push(`Entities: ${entities.join(', ')}`);
  if (input.viewNote) scope.push(`Map view: ${input.viewNote}`);
  if (events.length && input.eventsTotal !== undefined && input.eventsTotal !== events.length) {
    scope.push(`Events shown: ${events.length} of ${input.eventsTotal} (cropped to the map view)`);
  }
  if (scope.length) sections.push({ heading: 'Scope', lines: scope });

  if (input.include.story && events.length) {
    const story = buildStoryDoc(events, entities, null);
    for (const s of story.sections) sections.push(s.heading === 'Timeline' ? { heading: 'Story timeline', lines: s.lines } : s);
  }

  if (input.include.ai && input.aiSummary) {
    sections.push({
      heading: input.aiFocus ? `AI summary — focus: ${input.aiFocus}` : 'AI summary',
      lines: input.aiSummary.split(/\n{2,}/).map((p) => p.trim()).filter(Boolean),
    });
  }

  if (input.include.routeSequence && input.routeSequence?.length) {
    for (const g of input.routeSequence) {
      if (!g.entries.length) continue;
      sections.push({
        heading: input.routeSequence.length > 1 ? `Route sequence — ${g.entityId}` : 'Route sequence',
        lines: g.entries.map((e) => {
          const when = `${fmtWhen(e.time)}${e.endTime.getTime() !== e.time.getTime() ? ` → ${fmtTime(e.endTime)}` : ''}`;
          if (e.kind === 'camera') {
            const dist = e.distanceM !== undefined ? ` — ${Math.round(e.distanceM)} m from track` : '';
            const hits = e.hits && e.hits > 1 ? ` (${e.hits} hits)` : '';
            return `${e.seq}. Camera ${e.label}${e.categoryLabel ? ` [${e.categoryLabel}]` : ''}${hits} — ${when}${dist} — ${fmtLatLng(e.lat, e.lon)}`;
          }
          return `${e.seq}. Dwell ${e.label}${e.detail ? ` (${e.detail})` : ''} — ${when} — ${fmtLatLng(e.lat, e.lon)}`;
        }),
      });
    }
  }

  if (input.include.cameras && input.camerasPassed?.length) {
    sections.push({
      heading: `Cameras passed (${input.camerasPassed.length})`,
      lines: input.camerasPassed.map((c) => {
        const t = new Date(c.time);
        const dist = c.distanceM !== undefined ? ` — ${Math.round(c.distanceM)} m from track` : '';
        const hits = c.hits && c.hits > 1 ? ` (${c.hits} hits)` : '';
        return `${c.seq}. ${c.label}${c.category ? ` [${c.category}]` : ''}${hits} — ${fmtWhen(t)}${dist} — ${fmtLatLng(c.lat, c.lng)}${entities.length > 1 ? ` — ${c.entityId}` : ''}`;
      }),
    });
  }

  if (input.include.legend && input.legend) {
    const cats = input.legend.cameraCategories.filter((c) => c.count > 0);
    sections.push({
      heading: 'Legend',
      lines: [
        `Cameras in view: ${input.legend.cameraCount}`,
        ...cats.map((c) => `- ${c.label}: ${c.count}`),
      ],
    });
  }

  const span =
    events.length > 0 ? `${fmtWhen(events[0].startTime)} → ${fmtWhen(events[events.length - 1].endTime)}` : '';
  return {
    title: input.title?.trim() || 'Pathfinder product',
    subtitle: [entities.join(', '), span].filter(Boolean).join(' · ') || undefined,
    classification: input.classification?.trim() || undefined,
    sections,
  };
}

export function buildSentinelDoc(messages: NarrativeChatMessage[], scope?: string): HudExportDoc {
  const sections: HudExportSection[] = [];
  let pending: string | null = null;
  for (const m of messages) {
    if (m.role === 'user') {
      pending = m.content;
      continue;
    }
    sections.push({
      heading: pending ? `Q: ${pending}` : 'Sentinel',
      lines: m.content.split(/\n{2,}/).map((p) => p.trim()).filter(Boolean),
    });
    pending = null;
  }
  return { title: 'Sentinel insights', subtitle: scope, sections };
}

/* ------------------------------------------------------------------------ */
/* Text / markdown                                                           */
/* ------------------------------------------------------------------------ */

export function docToMarkdown(doc: HudExportDoc): string {
  const out: string[] = [];
  if (doc.classification) out.push(`**${doc.classification}**`, '');
  out.push(`# ${doc.title}`);
  if (doc.subtitle) out.push(`_${doc.subtitle}_`);
  out.push(`_Exported ${new Date().toLocaleString()}_`, '');
  for (const s of doc.sections) {
    out.push(`## ${s.heading}`, '');
    for (const l of s.lines) out.push(l.startsWith('-') || /^\d+\./.test(l) ? l : `${l}`);
    out.push('');
  }
  return out.join('\n');
}

export function docToPlainText(doc: HudExportDoc): string {
  return docToMarkdown(doc).replace(/^#+\s*/gm, '').replace(/^_(.*)_$/gm, '$1').replace(/\*\*/g, '');
}

export async function copyDocToClipboard(doc: HudExportDoc): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(docToPlainText(doc));
    return true;
  } catch {
    return false;
  }
}

export function downloadText(filename: string, text: string, mime = 'text/markdown') {
  const blob = new Blob([text], { type: `${mime};charset=utf-8` });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function downloadDocMarkdown(doc: HudExportDoc) {
  downloadText(`${safeName(doc.title)}-${stamp()}.md`, docToMarkdown(doc));
}

/* ------------------------------------------------------------------------ */
/* Standalone HTML                                                           */
/* ------------------------------------------------------------------------ */

const escapeHtml = (s: string) =>
  s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c] as string));
const inlineHtml = (s: string) => escapeHtml(s).replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
const BARE_HEADING_RE = /^(executive summary|key findings|actionable conclusions|summary|conclusions|findings)$/i;

/** Light markdown → HTML: `###` / bare headings, numbered + bulleted lists, paragraphs. */
function linesToHtml(lines: string[]): string {
  const out: string[] = [];
  let list: { tag: 'ul' | 'ol'; items: string[] } | null = null;
  const flush = () => {
    if (!list) return;
    out.push(`<${list.tag}>${list.items.map((i) => `<li>${i}</li>`).join('')}</${list.tag}>`);
    list = null;
  };
  for (const raw of lines) {
    for (const line of raw.split('\n')) {
      const t = line.trim();
      if (!t || t === '---') continue;
      const h = t.match(/^#{1,3}\s+(.+)/);
      if (h || BARE_HEADING_RE.test(t)) {
        flush();
        out.push(`<h3>${inlineHtml((h?.[1] ?? t).replace(/\*+/g, ''))}</h3>`);
        continue;
      }
      const ol = t.match(/^\d+[.)]\s+(.+)/);
      const ul = t.match(/^[-*•]\s+(.+)/);
      if (ol || ul) {
        const tag: 'ul' | 'ol' = ol ? 'ol' : 'ul';
        if (!list || list.tag !== tag) {
          flush();
          list = { tag, items: [] };
        }
        list.items.push(inlineHtml((ol ?? ul)![1]));
        continue;
      }
      flush();
      out.push(`<p>${inlineHtml(t)}</p>`);
    }
  }
  flush();
  return out.join('\n');
}

export interface HudHtmlOptions {
  /** Map view PNG data URL (optional figure). */
  mapImageDataUrl?: string | null;
  /** Caption under the map figure. */
  mapCaption?: string;
  /** Rendered panel PNG data URL (optional figure). */
  cardImageDataUrl?: string | null;
  /** Accent hex, e.g. #FF2332. */
  accent?: string;
}

/** Self-contained HTML page (inline CSS, images embedded as data URLs). */
export function docToHtml(
  doc: HudExportDoc,
  { mapImageDataUrl, mapCaption = 'Map view at export', cardImageDataUrl, accent = '#FF2332' }: HudHtmlOptions = {},
): string {
  const generated = new Date().toLocaleString();
  const sections = doc.sections
    .map((s) => `<section><h2>${escapeHtml(s.heading)}</h2>\n${linesToHtml(s.lines)}</section>`)
    .join('\n');
  const figure = (src: string | null | undefined, caption: string) =>
    src ? `<figure><img src="${src}" alt="${escapeHtml(caption)}"><figcaption>${escapeHtml(caption)}</figcaption></figure>` : '';
  const banner = doc.classification ? `<div class="banner">${escapeHtml(doc.classification)}</div>` : '';
  return `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width,initial-scale=1">
<title>${escapeHtml(doc.title)}${doc.subtitle ? ` — ${escapeHtml(doc.subtitle)}` : ''}</title>
<style>
  :root { --accent: ${accent}; --bg: #14151b; --panel: #1b1d24; --text: #e6e8ec; --muted: #aeb7c2; --border: rgba(174,183,194,0.28); }
  * { box-sizing: border-box; }
  body { margin: 0; background: var(--bg); color: var(--text); font: 15px/1.55 system-ui, "Segoe UI", Roboto, Helvetica, Arial, sans-serif; }
  .wrap { max-width: 960px; margin: 0 auto; padding: 32px 24px 64px; }
  header { border-top: 3px solid var(--accent); padding-top: 20px; margin-bottom: 28px; }
  h1 { margin: 0 0 6px; font-size: 28px; letter-spacing: 0.2px; }
  .sub { color: var(--muted); margin: 0; }
  .meta { color: var(--muted); font-size: 12px; margin-top: 6px; }
  section { border-left: 3px solid var(--accent); padding: 2px 0 2px 16px; margin: 0 0 28px; }
  h2 { font-size: 12px; letter-spacing: 0.12em; text-transform: uppercase; color: var(--accent); margin: 0 0 10px; }
  h3 { font-size: 15px; margin: 14px 0 6px; }
  p { margin: 0 0 10px; }
  ul, ol { margin: 0 0 10px; padding-left: 22px; }
  li { margin: 3px 0; }
  li::marker { color: var(--accent); }
  figure { margin: 0 0 28px; }
  img { max-width: 100%; height: auto; display: block; border: 1px solid var(--border); border-radius: 8px; }
  figcaption { color: var(--muted); font-size: 12px; margin-top: 6px; }
  .banner { background: var(--accent); color: #fff; text-align: center; font-weight: 700; letter-spacing: 0.14em; font-size: 12px; padding: 5px 8px; text-transform: uppercase; }
  @media print {
    body { background: #fff; color: #111; }
    :root { --muted: #555; --border: #ccc; }
    .wrap { padding: 0; max-width: none; }
    section, figure { break-inside: avoid; }
    .banner { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  }
</style>
</head>
<body>
${banner}
<div class="wrap">
  <header>
    <h1>${escapeHtml(doc.title)}</h1>
    ${doc.subtitle ? `<p class="sub">${escapeHtml(doc.subtitle)}</p>` : ''}
    <div class="meta">Exported ${escapeHtml(generated)} · Pathfinder</div>
  </header>
  ${figure(mapImageDataUrl, mapCaption)}
  ${sections}
  ${figure(cardImageDataUrl, `${doc.title} — panel as shown`)}
</div>
${banner}
</body>
</html>
`;
}

export async function downloadDocHtml(doc: HudExportDoc, opts: HudHtmlOptions & { includeMap?: boolean } = {}) {
  let mapImageDataUrl = opts.mapImageDataUrl ?? null;
  if (opts.includeMap && !mapImageDataUrl) {
    try {
      const shot = await captureMapScreenshot('pathfinder-map-container', undefined, false);
      mapImageDataUrl = shot?.dataUrl ?? null;
    } catch (e) {
      console.warn('Map capture skipped for HTML export:', e);
    }
  }
  downloadText(`${safeName(doc.title)}-${stamp()}.html`, docToHtml(doc, { ...opts, mapImageDataUrl }), 'text/html');
}

/* ------------------------------------------------------------------------ */
/* PDF                                                                       */
/* ------------------------------------------------------------------------ */

/** Text PDF (A4 portrait): title, optional map image, then every section with page breaks. */
export async function downloadDocPdf(
  doc: HudExportDoc,
  {
    mapImageDataUrl,
    mapImageAspect,
    accent = '#FF2332',
    includeMap = true,
    mapFullPage = false,
  }: HudHtmlOptions & {
    includeMap?: boolean;
    /** Width / height of `mapImageDataUrl`; needed to size a supplied image correctly. */
    mapImageAspect?: number;
    /** Put the map capture on its own landscape page before the text (for products). */
    mapFullPage?: boolean;
  } = {},
) {
  const { default: JsPDF } = await import('jspdf');
  const pdf = new JsPDF({ unit: 'mm', format: 'a4' });
  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();
  const margin = 16;
  const textW = pageW - margin * 2;
  const rgb = /^#?([0-9a-f]{6})$/i.exec(accent)?.[1] ?? 'FF2332';
  const accentRgb: [number, number, number] = [parseInt(rgb.slice(0, 2), 16), parseInt(rgb.slice(2, 4), 16), parseInt(rgb.slice(4, 6), 16)];
  const bannerH = doc.classification ? 7 : 0;
  /** Classification banner top and bottom of the current page. */
  const drawBanner = () => {
    if (!doc.classification) return;
    const w = pdf.internal.pageSize.getWidth();
    const h = pdf.internal.pageSize.getHeight();
    pdf.setFillColor(...accentRgb);
    pdf.rect(0, 0, w, bannerH, 'F');
    pdf.rect(0, h - bannerH, w, bannerH, 'F');
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(9);
    pdf.setTextColor(255, 255, 255);
    pdf.text(doc.classification.toUpperCase(), w / 2, 4.8, { align: 'center' });
    pdf.text(doc.classification.toUpperCase(), w / 2, h - bannerH + 4.8, { align: 'center' });
  };
  let y = margin + bannerH;
  const ensure = (h: number) => {
    if (y + h > pageH - margin - bannerH) {
      pdf.addPage();
      drawBanner();
      y = margin + bannerH;
    }
  };

  let mapUrl = mapImageDataUrl ?? null;
  let aspect = mapImageAspect ?? 16 / 9;
  if (includeMap && !mapUrl) {
    try {
      const shot = await captureMapScreenshot('pathfinder-map-container', undefined, false);
      mapUrl = shot?.dataUrl ?? null;
      if (shot?.aspectRatio) aspect = shot.aspectRatio;
    } catch (e) {
      console.warn('Map capture skipped for PDF export:', e);
    }
  }

  if (mapUrl && mapFullPage) {
    // Landscape page sized to the capture, then continue in portrait.
    pdf.deletePage(1);
    pdf.addPage('a4', 'landscape');
    drawBanner();
    const lw = pdf.internal.pageSize.getWidth();
    const lh = pdf.internal.pageSize.getHeight();
    const availW = lw - margin * 2;
    const availH = lh - margin * 2 - bannerH * 2 - 10;
    let w = availW;
    let h = w / aspect;
    if (h > availH) {
      h = availH;
      w = h * aspect;
    }
    const x = (lw - w) / 2;
    const top = margin + bannerH;
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(14);
    pdf.setTextColor(20, 20, 20);
    pdf.text(doc.title, margin, top + 2);
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(8);
    pdf.setTextColor(120, 120, 120);
    pdf.text(`Map view at export · ${new Date().toLocaleString()}`, lw - margin, top + 2, { align: 'right' });
    pdf.addImage(mapUrl, 'PNG', x, top + 6, w, h);
    pdf.addPage('a4', 'portrait');
    drawBanner();
    y = margin + bannerH;
    mapUrl = null;
  } else {
    drawBanner();
  }

  pdf.setFillColor(...accentRgb);
  pdf.rect(0, bannerH, pageW, 2.5, 'F');
  y += 4;
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(20);
  pdf.setTextColor(20, 20, 20);
  pdf.text(doc.title, margin, y);
  y += 8;
  if (doc.subtitle) {
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(11);
    pdf.setTextColor(90, 90, 90);
    const sub = pdf.splitTextToSize(doc.subtitle, textW) as string[];
    pdf.text(sub, margin, y);
    y += sub.length * 5 + 1;
  }
  pdf.setFontSize(9);
  pdf.setTextColor(120, 120, 120);
  pdf.text(`Exported ${new Date().toLocaleString()} · Pathfinder`, margin, y);
  y += 8;

  if (mapUrl) {
    // Keep the aspect ratio: shrink the width when the image would be too tall.
    let w = textW;
    let h = w / aspect;
    const maxH = pageH * 0.45;
    if (h > maxH) {
      h = maxH;
      w = h * aspect;
    }
    ensure(h + 6);
    pdf.addImage(mapUrl, 'PNG', margin + (textW - w) / 2, y, w, h);
    y += h + 8;
  }

  for (const s of doc.sections) {
    ensure(14);
    pdf.setFillColor(...accentRgb);
    pdf.rect(margin, y - 4, 1.2, 6, 'F');
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(10);
    pdf.setTextColor(...accentRgb);
    pdf.text(s.heading.toUpperCase(), margin + 4, y);
    y += 7;
    pdf.setTextColor(30, 30, 30);
    for (const raw of s.lines) {
      for (const line of raw.split('\n')) {
        const t = line.trim();
        if (!t || t === '---') continue;
        const h = t.match(/^#{1,3}\s+(.+)/);
        if (h || BARE_HEADING_RE.test(t)) {
          ensure(8);
          pdf.setFont('helvetica', 'bold');
          pdf.setFontSize(11);
          pdf.text((h?.[1] ?? t).replace(/\*+/g, ''), margin, y + 1);
          y += 7;
          continue;
        }
        const bullet = t.match(/^(?:[-*•]|\d+[.)])\s+(.+)/);
        const body = (bullet ? bullet[1] : t).replace(/\*\*/g, '');
        const indent = bullet ? 5 : 0;
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(10);
        const wrapped = pdf.splitTextToSize(body, textW - indent) as string[];
        const h2 = wrapped.length * 4.6 + 1.5;
        ensure(h2);
        if (bullet) {
          const label = /^\d/.test(t) ? t.match(/^(\d+)/)![1] + '.' : '•';
          pdf.text(label, margin, y);
        }
        pdf.text(wrapped, margin + indent, y);
        y += h2;
      }
    }
    y += 5;
  }
  pdf.save(`${safeName(doc.title)}-${stamp()}.pdf`);
}

/* ------------------------------------------------------------------------ */
/* PNG of the card                                                           */
/* ------------------------------------------------------------------------ */

/**
 * Render a HUD card to a PNG. The card is cloned off-screen with scroll
 * clipping removed so the full thread / timeline is captured, not just the
 * visible viewport.
 */
export async function hudCardToPngDataUrl(card: HTMLElement, solidBackground: string): Promise<string | null> {
  const html2canvas = await loadHtml2Canvas();
  const rect = card.getBoundingClientRect();
  const clone = card.cloneNode(true) as HTMLElement;
  const host = document.createElement('div');
  host.style.cssText = 'position:fixed;left:-20000px;top:0;z-index:-1;pointer-events:none;';
  Object.assign(clone.style, {
    position: 'static',
    left: 'auto',
    top: 'auto',
    right: 'auto',
    bottom: 'auto',
    transform: 'none',
    width: `${Math.round(rect.width)}px`,
    maxWidth: 'none',
    maxHeight: 'none',
    height: 'auto',
    overflow: 'visible',
    backdropFilter: 'none',
    backgroundColor: solidBackground,
    boxShadow: 'none',
  } as CSSStyleDeclaration);
  // Expand every scroll region.
  clone.querySelectorAll<HTMLElement>('*').forEach((el) => {
    const cs = el.style;
    const computed = window.getComputedStyle(el);
    if (computed.overflowY === 'auto' || computed.overflowY === 'scroll' || computed.overflowX === 'auto') {
      cs.overflow = 'visible';
      cs.maxHeight = 'none';
      cs.height = 'auto';
    }
  });
  // Hide header controls (buttons) in the export.
  clone.querySelectorAll<HTMLElement>('[data-hud-controls]').forEach((el) => {
    el.style.display = 'none';
  });
  host.appendChild(clone);
  document.body.appendChild(host);
  try {
    const canvas = await html2canvas(clone, { backgroundColor: null, scale: 2, useCORS: true, logging: false });
    return canvas.toDataURL('image/png');
  } catch (e) {
    console.error('HUD PNG export failed:', e);
    return null;
  } finally {
    host.remove();
  }
}

export function downloadDataUrl(filename: string, dataUrl: string) {
  const a = document.createElement('a');
  a.href = dataUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
}

export async function downloadHudCardPng(card: HTMLElement, title: string, solidBackground: string) {
  const url = await hudCardToPngDataUrl(card, solidBackground);
  if (url) downloadDataUrl(`${safeName(title)}-${stamp()}.png`, url);
  return Boolean(url);
}

/* ------------------------------------------------------------------------ */
/* PowerPoint                                                                */
/* ------------------------------------------------------------------------ */

const SLIDE_W = 13.333;
const SLIDE_H = 7.5;
const PPT_CHARS_PER_SLIDE = 1500;

/** Split long sections across slides, keeping headings with their text. */
function paginate(doc: HudExportDoc): Array<{ heading: string; lines: string[] }> {
  const pages: Array<{ heading: string; lines: string[] }> = [];
  for (const s of doc.sections) {
    let cur: string[] = [];
    let len = 0;
    let part = 1;
    const flush = () => {
      if (cur.length === 0) return;
      pages.push({ heading: part === 1 ? s.heading : `${s.heading} (cont.)`, lines: cur });
      cur = [];
      len = 0;
      part++;
    };
    for (const line of s.lines) {
      if (len + line.length > PPT_CHARS_PER_SLIDE && cur.length > 0) flush();
      cur.push(line);
      len += line.length;
    }
    flush();
  }
  return pages;
}

export interface HudPptxOptions {
  doc: HudExportDoc;
  /** Rendered PNG of the card (optional slide). */
  cardImageDataUrl?: string | null;
  /** Include a slide with the current map view. */
  includeMap?: boolean;
  /** Brand-ish colors (hex without #). */
  colors?: { bg: string; text: string; muted: string; accent: string };
}

export async function exportHudDocToPowerPoint({
  doc,
  cardImageDataUrl,
  includeMap = true,
  colors = { bg: '14151B', text: 'E6E8EC', muted: 'AEB7C2', accent: 'FF2332' },
}: HudPptxOptions): Promise<void> {
  const PptxGenJS = await loadPptxGenJS();
  const pptx = new PptxGenJS();
  pptx.layout = 'LAYOUT_WIDE';
  pptx.author = 'Pathfinder';
  pptx.title = doc.title;

  const generated = new Date().toLocaleString();

  // Title
  const title = pptx.addSlide();
  title.background = { color: colors.bg };
  title.addShape(pptx.ShapeType.rect, { x: 0, y: 0, w: SLIDE_W, h: 0.12, fill: { color: colors.accent }, line: { color: colors.accent } });
  title.addText(doc.title, { x: 0.75, y: 2.4, w: SLIDE_W - 1.5, h: 1, fontSize: 40, bold: true, color: colors.text });
  if (doc.subtitle) title.addText(doc.subtitle, { x: 0.75, y: 3.4, w: SLIDE_W - 1.5, h: 0.6, fontSize: 20, color: colors.muted });
  title.addText(`Generated ${generated}`, { x: 0.75, y: SLIDE_H - 1, w: SLIDE_W - 1.5, h: 0.4, fontSize: 12, color: colors.muted });

  // Map context
  if (includeMap) {
    try {
      const shot = await captureMapScreenshot('pathfinder-map-container', undefined, false);
      if (shot?.dataUrl) {
        const s = pptx.addSlide();
        s.background = { color: colors.bg };
        s.addText('Map view', { x: 0.5, y: 0.3, w: SLIDE_W - 1, h: 0.5, fontSize: 20, bold: true, color: colors.text });
        const maxW = SLIDE_W - 1;
        const maxH = SLIDE_H - 1.3;
        let w = maxW;
        let h = w / shot.aspectRatio;
        if (h > maxH) {
          h = maxH;
          w = h * shot.aspectRatio;
        }
        s.addImage({ data: shot.dataUrl, x: (SLIDE_W - w) / 2, y: 0.9, w, h });
      }
    } catch (e) {
      console.warn('Map capture skipped for HUD export:', e);
    }
  }

  // Content
  for (const page of paginate(doc)) {
    const s = pptx.addSlide();
    s.background = { color: colors.bg };
    s.addShape(pptx.ShapeType.rect, { x: 0.5, y: 0.95, w: 0.08, h: 0.5, fill: { color: colors.accent }, line: { color: colors.accent } });
    s.addText(page.heading, { x: 0.7, y: 0.3, w: SLIDE_W - 1.2, h: 0.6, fontSize: 22, bold: true, color: colors.text });
    s.addText(
      page.lines.map((l) => ({ text: l, options: { breakLine: true, paraSpaceAfter: 6 } })),
      { x: 0.7, y: 1.1, w: SLIDE_W - 1.4, h: SLIDE_H - 1.7, fontSize: 14, color: colors.text, valign: 'top', fit: 'shrink' },
    );
  }

  // Card image
  if (cardImageDataUrl) {
    const s = pptx.addSlide();
    s.background = { color: colors.bg };
    s.addText(`${doc.title} — panel`, { x: 0.5, y: 0.3, w: SLIDE_W - 1, h: 0.5, fontSize: 20, bold: true, color: colors.text });
    const img = new Image();
    await new Promise<void>((resolve) => {
      img.onload = () => resolve();
      img.onerror = () => resolve();
      img.src = cardImageDataUrl;
    });
    const ar = img.width && img.height ? img.width / img.height : 0.6;
    const maxW = SLIDE_W - 1;
    const maxH = SLIDE_H - 1.3;
    let h = maxH;
    let w = h * ar;
    if (w > maxW) {
      w = maxW;
      h = w / ar;
    }
    s.addImage({ data: cardImageDataUrl, x: (SLIDE_W - w) / 2, y: 0.9, w, h });
  }

  await pptx.writeFile({ fileName: `${safeName(doc.title)}-${stamp()}.pptx` });
}
